import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Card,
  Row,
  Col,
  Button,
  Tag,
  Drawer,
  InputNumber,
  Switch,
  Tooltip,
  Input,
  message,
  Skeleton,
  Alert,
  Badge,
  Spin,
  Select,
  Space,
} from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import {
  ArrowLeft,
  Save,
  Calendar,
  RefreshCw,
  RotateCcw,
  Edit3,
  Sliders,
  ChevronLeft,
  ChevronRight,
  Box as Bed,
  BarChart2,
  Slash,
  Shield,
  DollarSign,
  X,
  Check,
  Trash2,
  Info,
} from "react-feather";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import axios from "axios";
import BeBulkUpdateDrawer from "./BeBulkUpdateDrawer";

const DEFAULT_CURRENCY = "INR";
const DEFAULT_CALENDAR_WINDOW_DAYS = 14;

const buildRangeDays = (startMoment, count = DEFAULT_CALENDAR_WINDOW_DAYS) => {
  const start = moment(startMoment).startOf("day");
  return Array.from({ length: count }, (_, index) => start.clone().add(index, "day"));
};

const useQuery = () => {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
};

const safeInt = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const safeNumber = (v, fallback = null) => {
  if (v === "" || v === null || v === undefined) return fallback;
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const currencySymbol = (code) => {
  if (code === "INR") return "₹";
  if (code === "GBP") return "£";
  if (code === "USD") return "$";
  if (code === "EUR") return "€";
  return `${code || ""} `;
};

const compactMoney = (value, currency = DEFAULT_CURRENCY) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return null;
  return `${currencySymbol(currency)}${amount.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
};

const buildMonthDays = (monthMoment) => {
  const start = monthMoment.clone().startOf("month");
  const end = monthMoment.clone().endOf("month");
  const days = [];
  const cursor = start.clone();
  while (cursor.isSameOrBefore(end, "day")) {
    days.push(cursor.clone());
    cursor.add(1, "day");
  }
  return days;
};

// default map for a month: dateKey -> values
const buildDefaultInventoryMap = (days, totalRooms = 0) => {
  const map = {};
  days.forEach((d) => {
    const key = d.format("YYYY-MM-DD");
    map[key] = {
      allotment: Math.max(0, safeInt(totalRooms, 0)),
      stopSell: false,
      minStay: 1,
      cta: false, // closed_to_arrival
      ctd: false, // closed_to_departure
      rateCardId: null,
      overrideRate: null, // legacy single-rate override
      currencyCode: DEFAULT_CURRENCY,
      rateOverrides: {},
      notes: "",
    };
  });
  return map;
};

const normalizeRoomTypes = (raw) => {
  const arr = Array.isArray(raw) ? raw : [];
  return arr
    .map((rt) => ({
      id: Number(rt.room_type_id ?? rt.id),
      name: rt.type_name ?? rt.name ?? "Room Type",
      total_rooms: safeInt(rt.total_rooms, 0),
    }))
    .filter((x) => !!x.id);
};

const normalizeBeRateOptions = (raw) => (Array.isArray(raw) ? raw : [])
  .filter((r) => String(r?.source || "").toUpperCase() === "BE" && r?.status !== false)
  .map((r) => ({ ...r, rate_id: Number(r.rate_id), occ1_rate: safeNumber(r.occ1_rate, 0), occ2_rate: safeNumber(r.occ2_rate, 0), is_guest_wise_rate: !!r.is_guest_wise_rate, guest_wise_rates: r?.guest_wise_rates && typeof r.guest_wise_rates === "object" ? r.guest_wise_rates : {} }))
  .filter((r) => !!r.rate_id);

const swalToast = Swal.mixin({
  toast: true,
  position: "top-end",
  showConfirmButton: false,
  timer: 3200,
  timerProgressBar: true,
});

const BeManualInventoryCalendar = () => {
  const navigate = useNavigate();
  const q = useQuery();
  const returnTo = q.get("return") || "/booking-engine/room-allocation";

  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:3000/api/hms";
  const token = localStorage.getItem("token");

  const authHeaders = useMemo(
    () => ({ headers: { Authorization: token ? `Bearer ${token}` : "" } }),
    [token]
  );

  const ensureAuth = () => {
    if (!token) {
      message.error("You are not logged in.");
      return false;
    }
    return true;
  };

  // UX states
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [softInit, setSoftInit] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [modifiedDates, setModifiedDates] = useState(() => new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const lastServerSnapRef = useRef(null);

  // Data
  const [roomTypes, setRoomTypes] = useState([]);
  const [selectedRoomTypeId, setSelectedRoomTypeId] = useState(null);
  const [rateOptions, setRateOptions] = useState([]);
  const [stayPricingMethod, setStayPricingMethod] = useState("nightly");
  const [savingPricingMethod, setSavingPricingMethod] = useState(false);

  const businessDate = moment().startOf("day");
  const [month, setMonth] = useState(businessDate.clone().startOf("month"));
  // Channel Manager parity: the operational calendar opens from today and
  // moves in continuous 14-day windows instead of forcing a calendar month.
  const [calendarStart, setCalendarStart] = useState(businessDate.clone());
  const [rangeLoading, setRangeLoading] = useState(false);

  // inventory: dateKey -> values (for the selected room type and month)
  const [inventory, setInventory] = useState({});
  // Cross-month bulk edits are staged here so future dates can be selected without
  // losing the review-before-save workflow of the monthly calendar.
  const [extraInventory, setExtraInventory] = useState({});

  const days = useMemo(() => buildMonthDays(month), [month]);
  const visibleDays = useMemo(
    () => buildRangeDays(calendarStart, DEFAULT_CALENDAR_WINDOW_DAYS),
    [calendarStart]
  );
  const calendarEnd = useMemo(
    () => calendarStart.clone().add(DEFAULT_CALENDAR_WINDOW_DAYS - 1, "day"),
    [calendarStart]
  );

  // Edit drawer state (one day)
  const [open, setOpen] = useState(false);
  const [editDateKey, setEditDateKey] = useState(null);
  const [editDateLoading, setEditDateLoading] = useState(false);

  const selectedRoomType = useMemo(
    () => roomTypes.find((r) => r.id === selectedRoomTypeId) || null,
    [roomTypes, selectedRoomTypeId]
  );

  const isVisibleMonthDate = (dateKey) => String(dateKey || "").slice(0, 7) === month.format("YYYY-MM");

  const getInventoryCell = (dateKey) => {
    if (!dateKey) return {};
    return (isVisibleMonthDate(dateKey) ? inventory[dateKey] : extraInventory[dateKey]) || {};
  };

  const editCell = useMemo(() => {
    if (!editDateKey) return {};
    return (String(editDateKey).slice(0, 7) === month.format("YYYY-MM")
      ? inventory[editDateKey]
      : extraInventory[editDateKey]) || {};
  }, [editDateKey, inventory, extraInventory, month]);

  const getDateRateSummary = (cell = {}) => {
    const values = [];
    const currency = String(cell.currencyCode || DEFAULT_CURRENCY).toUpperCase();

    if (cell.overrideRate !== null && cell.overrideRate !== "" && cell.overrideRate !== undefined) {
      const legacy = Number(cell.overrideRate);
      if (Number.isFinite(legacy) && legacy >= 0) values.push({ amount: legacy, currency, source: "override" });
    }

    Object.values(cell.rateOverrides || {}).forEach((row) => {
      const rowCurrency = String(row?.currency_code || currency || DEFAULT_CURRENCY).toUpperCase();
      [row?.occ1_rate, row?.occ2_rate, ...Object.values(row?.guest_wise_rates || {})].forEach((v) => {
        const n = Number(v);
        if (v !== null && v !== "" && v !== undefined && Number.isFinite(n) && n >= 0) {
          values.push({ amount: n, currency: rowCurrency, source: "override" });
        }
      });
    });

    if (values.length) {
      const lowest = values.sort((a, b) => a.amount - b.amount)[0];
      return { label: compactMoney(lowest.amount, lowest.currency), source: "Date override", isOverride: true };
    }

    const masterValues = [];
    (rateOptions || []).forEach((row) => {
      [row?.occ1_rate, row?.occ2_rate, ...Object.values(row?.guest_wise_rates || {})].forEach((v) => {
        const n = Number(v);
        if (v !== null && v !== "" && v !== undefined && Number.isFinite(n) && n >= 0) masterValues.push(n);
      });
    });

    if (masterValues.length) {
      const lowest = Math.min(...masterValues);
      return {
        label: compactMoney(lowest, DEFAULT_CURRENCY),
        source: (rateOptions || []).length > 1 ? "Master · from" : "Master rate",
        isOverride: false,
      };
    }

    return { label: "—", source: "No BE rate", isOverride: false };
  };

  const openEdit = (dateKey) => {
    setEditDateKey(dateKey);
    setOpen(true);
  };

  const patchDay = (dateKey, patch) => {
    const setTarget = isVisibleMonthDate(dateKey) ? setInventory : setExtraInventory;
    setTarget((prev) => ({
      ...prev,
      [dateKey]: {
        ...(prev[dateKey] || {}),
        ...patch,
      },
    }));
    setModifiedDates((prev) => {
      const next = new Set(prev);
      next.add(dateKey);
      return next;
    });
    setDirty(true);
  };

  const patchRateOverride = (dateKey, rateId, patch) => {
    const cell = getInventoryCell(dateKey);
    const current = cell.rateOverrides || {};
    const previous = current[String(rateId)] || {
      rate_id: Number(rateId),
      currency_code: cell.currencyCode || DEFAULT_CURRENCY,
    };
    const nextRow = { ...previous, ...patch, rate_id: Number(rateId) };
    const hasValue =
      (nextRow.occ1_rate !== null && nextRow.occ1_rate !== "" && nextRow.occ1_rate !== undefined) ||
      (nextRow.occ2_rate !== null && nextRow.occ2_rate !== "" && nextRow.occ2_rate !== undefined) ||
      Object.values(nextRow.guest_wise_rates || {}).some((v) => v !== null && v !== "" && v !== undefined);
    const nextOverrides = { ...current };
    if (hasValue) nextOverrides[String(rateId)] = nextRow;
    else delete nextOverrides[String(rateId)];
    patchDay(dateKey, { rateOverrides: nextOverrides });
  };

  const applyBulkToCalendar = async ({ dateKeys = [], patch = {}, rateOverrides = [], meta = {} }) => {
    if (!dateKeys.length || (!Object.keys(patch).length && !rateOverrides.length)) return;

    const currentMonthKey = month.format("YYYY-MM");
    const currentMonthDates = dateKeys.filter((dateKey) => String(dateKey).slice(0, 7) === currentMonthKey);
    const futureDates = dateKeys.filter((dateKey) => String(dateKey).slice(0, 7) !== currentMonthKey);

    const mergeBulkPatch = (currentDay = {}) => {
      const mergedRateOverrides = { ...(currentDay.rateOverrides || {}) };

      rateOverrides.forEach((ro) => {
        const key = String(Number(ro.rate_id));
        if (!key || key === "NaN") return;
        const existing = mergedRateOverrides[key] || {
          rate_id: Number(ro.rate_id),
          currency_code: currentDay.currencyCode || DEFAULT_CURRENCY,
        };
        const mergedGuests = { ...(existing.guest_wise_rates || {}) };
        Object.entries(ro.guest_wise_rates || {}).forEach(([guestCount, value]) => {
          if (value !== null && value !== "" && value !== undefined) mergedGuests[String(guestCount)] = Number(value);
        });

        const merged = {
          ...existing,
          rate_id: Number(ro.rate_id),
          currency_code: ro.currency_code || existing.currency_code || DEFAULT_CURRENCY,
          guest_wise_rates: mergedGuests,
        };
        if (ro.occ1_rate !== null && ro.occ1_rate !== "" && ro.occ1_rate !== undefined) merged.occ1_rate = Number(ro.occ1_rate);
        if (ro.occ2_rate !== null && ro.occ2_rate !== "" && ro.occ2_rate !== undefined) merged.occ2_rate = Number(ro.occ2_rate);
        mergedRateOverrides[key] = merged;
      });

      return {
        ...currentDay,
        ...patch,
        rateOverrides: mergedRateOverrides,
      };
    };

    if (currentMonthDates.length) {
      setInventory((prev) => {
        const next = { ...(prev || {}) };
        currentMonthDates.forEach((dateKey) => {
          next[dateKey] = mergeBulkPatch(next[dateKey] || {});
        });
        return next;
      });
    }

    if (futureDates.length) {
      try {
        const sorted = [...futureDates].sort();
        const startDate = sorted[0];
        const endDate = sorted[sorted.length - 1];
        const { rows, rateOverrides: savedRateOverrides } = await fetchMonthCalendar(
          selectedRoomTypeId,
          startDate,
          endDate
        );

        const totalRooms = Math.max(0, safeInt(selectedRoomType?.total_rooms, 0));
        const baseMap = {};
        futureDates.forEach((dateKey) => {
          baseMap[dateKey] = {
            allotment: totalRooms,
            stopSell: false,
            minStay: 1,
            cta: false,
            ctd: false,
            rateCardId: null,
            overrideRate: null,
            currencyCode: DEFAULT_CURRENCY,
            rateOverrides: {},
            notes: "",
          };
        });

        (rows || []).forEach((r) => {
          const key = String(r.inventory_date).slice(0, 10);
          if (!baseMap[key]) return;
          baseMap[key] = {
            ...baseMap[key],
            allotment: Math.max(0, safeInt(r.allotment, baseMap[key].allotment)),
            stopSell: !!r.stop_sell,
            minStay: Math.max(1, safeInt(r.min_stay, 1)),
            cta: !!r.closed_to_arrival,
            ctd: !!r.closed_to_departure,
            rateCardId: r.rate_card_id != null ? Number(r.rate_card_id) : null,
            overrideRate: r.override_rate != null ? Number(r.override_rate) : null,
            currencyCode: String(r.currency_code || DEFAULT_CURRENCY),
            notes: String(r.notes || ""),
          };
        });

        (savedRateOverrides || []).forEach((r) => {
          const key = String(r.inventory_date).slice(0, 10);
          if (!baseMap[key]) return;
          baseMap[key] = {
            ...baseMap[key],
            rateOverrides: {
              ...(baseMap[key].rateOverrides || {}),
              [String(Number(r.rate_id))]: {
                rate_id: Number(r.rate_id),
                occ1_rate: r.occ1_rate == null ? null : Number(r.occ1_rate),
                occ2_rate: r.occ2_rate == null ? null : Number(r.occ2_rate),
                guest_wise_rates: r.guest_wise_rates && typeof r.guest_wise_rates === "object" ? r.guest_wise_rates : {},
                currency_code: String(r.currency_code || DEFAULT_CURRENCY),
              },
            },
          };
        });

        setExtraInventory((prev) => {
          const next = { ...(prev || {}) };
          futureDates.forEach((dateKey) => {
            const serverDay = baseMap[dateKey] || {};
            const alreadyStaged = next[dateKey] || {};
            next[dateKey] = mergeBulkPatch({ ...serverDay, ...alreadyStaged });
          });
          return next;
        });
      } catch (err) {
        console.error(err);
        message.error(err?.response?.data?.message || "Could not stage future-month dates. Please try again.");
        throw err;
      }
    }

    setModifiedDates((prev) => {
      const next = new Set(prev);
      dateKeys.forEach((dateKey) => next.add(dateKey));
      return next;
    });
    setDirty(true);

    const monthCount = new Set(dateKeys.map((dateKey) => String(dateKey).slice(0, 7))).size;
    swalToast.fire({
      icon: "success",
      title: monthCount > 1
        ? `${meta.affectedCount || dateKeys.length} dates staged across ${monthCount} months`
        : `${meta.affectedCount || dateKeys.length} dates staged for review`,
    });
  };

  const validateBeforeSave = () => {
    if (!selectedRoomTypeId) {
      message.error("Please select a room type.");
      return false;
    }

    const valuesToCheck = { ...inventory, ...extraInventory };
    for (const [dateKey, v] of Object.entries(valuesToCheck)) {
      const allotment = Math.max(0, safeInt(v?.allotment, 0));
      const minStay = Math.max(1, safeInt(v?.minStay, 1));
      if (minStay > 30) {
        message.error(`Min stay too high on ${dateKey}.`);
        return false;
      }
      if (allotment > 999) {
        message.error(`Allotment too high on ${dateKey}.`);
        return false;
      }
    }

    return true;
  };

  const toSaveItem = (dateKey, v = {}) => ({
    be_room_type_id: Number(selectedRoomTypeId),
    inventory_date: dateKey,
    allotment: Math.max(0, safeInt(v.allotment, 0)),
    stop_sell: !!v.stopSell,
    min_stay: Math.max(1, safeInt(v.minStay, 1)),
    closed_to_arrival: !!v.cta,
    closed_to_departure: !!v.ctd,
    rate_card_id: v.rateCardId != null && v.rateCardId !== "" ? Number(v.rateCardId) : null,
    override_rate: v.overrideRate != null && v.overrideRate !== "" ? Number(v.overrideRate) : null,
    currency_code: String(v.currencyCode || DEFAULT_CURRENCY).trim() || DEFAULT_CURRENCY,
    rate_overrides: Object.values(v.rateOverrides || {}).map((ro) => ({
      rate_id: Number(ro.rate_id),
      occ1_rate: ro.occ1_rate == null || ro.occ1_rate === "" ? null : Number(ro.occ1_rate),
      occ2_rate: ro.occ2_rate == null || ro.occ2_rate === "" ? null : Number(ro.occ2_rate),
      guest_wise_rates: ro.guest_wise_rates || {},
      currency_code: String(ro.currency_code || v.currencyCode || DEFAULT_CURRENCY).toUpperCase(),
    })),
    notes: String(v.notes || "").slice(0, 500),
  });

  const buildSavePayload = () => {
    const currentMonthItems = days.map((d) => {
      const key = d.format("YYYY-MM-DD");
      return toSaveItem(key, inventory[key] || {});
    });

    const extraItems = Object.entries(extraInventory || {})
      .filter(([dateKey]) => modifiedDates.has(dateKey))
      .map(([dateKey, values]) => toSaveItem(dateKey, values || {}));

    return { items: [...currentMonthItems, ...extraItems] };
  };

  const fetchRoomTypes = async () => {
    const res = await axios.get(`${apiUrl}/roomtype/listings/`, authHeaders);
    const rtRaw = res?.data?.roomTypes || res?.data?.data || res?.data || [];
    const normalized = normalizeRoomTypes(rtRaw);
    return normalized;
  };

  const fetchRateOptions = async (roomTypeId) => {
    const res = await axios.get(`${apiUrl}/booking-engine-settings/manual-rate-options`, {
      ...authHeaders,
      params: { roomTypeId },
    });
    const raw = res?.data?.data || [];
    const normalized = normalizeBeRateOptions(raw);
    setRateOptions(normalized);
    return normalized;
  };

  const fetchPricingSettings = async () => {
    try {
      const res = await axios.get(`${apiUrl}/booking-engine-settings/pricing-settings`, authHeaders);
      const method = res?.data?.stay_pricing_method === "check_in" ? "check_in" : "nightly";
      setStayPricingMethod(method);
      return method;
    } catch (err) {
      console.warn("Pricing settings unavailable; using nightly default", err);
      setStayPricingMethod("nightly");
      return "nightly";
    }
  };

  const saveStayPricingMethod = async (method) => {
    const next = method === "check_in" ? "check_in" : "nightly";
    setSavingPricingMethod(true);
    try {
      await axios.put(`${apiUrl}/booking-engine-settings/pricing-settings`, { stay_pricing_method: next }, authHeaders);
      setStayPricingMethod(next);
      swalToast.fire({ icon: "success", title: next === "nightly" ? "Nightly pricing enabled" : "Check-in rate pricing enabled" });
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to update stay pricing method.");
    } finally { setSavingPricingMethod(false); }
  };

  const fetchMonthCalendar = async (roomTypeId, startDate, endDate) => {
    const res = await axios.get(`${apiUrl}/booking-engine-settings/manual-calendar`, { ...authHeaders, params: { roomTypeId, startDate, endDate } });
    return { rows: Array.isArray(res?.data?.data) ? res.data.data : [], rateOverrides: Array.isArray(res?.data?.rateOverrides) ? res.data.rateOverrides : [] };
  };

  // Load only the part of the rolling 14-day window that sits outside the
  // base month kept in `inventory`. This preserves the original component's
  // save/bulk/edit model while allowing a continuous Aug -> Sep -> Oct view.
  const hydrateVisibleRange = async ({
    roomTypeId,
    startMoment = calendarStart,
    roomTypeList = roomTypes,
    preserveStaged = true,
  }) => {
    if (!roomTypeId) return;

    const rangeDays = buildRangeDays(startMoment, DEFAULT_CALENDAR_WINDOW_DAYS);
    const baseMonthKey = month.format("YYYY-MM");
    const externalDays = rangeDays.filter(
      (d) => d.format("YYYY-MM") !== baseMonthKey
    );

    if (!externalDays.length) return;

    const startDate = externalDays[0].format("YYYY-MM-DD");
    const endDate = externalDays[externalDays.length - 1].format("YYYY-MM-DD");
    const { rows, rateOverrides: savedRateOverrides } = await fetchMonthCalendar(
      roomTypeId,
      startDate,
      endDate
    );

    const rt = (roomTypeList || []).find((x) => Number(x.id) === Number(roomTypeId));
    const totalRooms = Math.max(0, safeInt(rt?.total_rooms, 0));
    const loaded = buildDefaultInventoryMap(externalDays, totalRooms);

    (rows || []).forEach((r) => {
      const key = String(r.inventory_date).slice(0, 10);
      if (!loaded[key]) return;
      loaded[key] = {
        ...loaded[key],
        allotment: Math.max(0, safeInt(r.allotment, loaded[key].allotment)),
        stopSell: !!r.stop_sell,
        minStay: Math.max(1, safeInt(r.min_stay, 1)),
        cta: !!r.closed_to_arrival,
        ctd: !!r.closed_to_departure,
        rateCardId: r.rate_card_id != null ? Number(r.rate_card_id) : null,
        overrideRate: r.override_rate != null ? Number(r.override_rate) : null,
        currencyCode: String(r.currency_code || DEFAULT_CURRENCY),
        notes: String(r.notes || ""),
      };
    });

    (savedRateOverrides || []).forEach((r) => {
      const key = String(r.inventory_date).slice(0, 10);
      if (!loaded[key]) return;
      loaded[key] = {
        ...loaded[key],
        rateOverrides: {
          ...(loaded[key].rateOverrides || {}),
          [String(Number(r.rate_id))]: {
            rate_id: Number(r.rate_id),
            occ1_rate: r.occ1_rate == null ? null : Number(r.occ1_rate),
            occ2_rate: r.occ2_rate == null ? null : Number(r.occ2_rate),
            guest_wise_rates:
              r.guest_wise_rates && typeof r.guest_wise_rates === "object"
                ? r.guest_wise_rates
                : {},
            currency_code: String(r.currency_code || DEFAULT_CURRENCY),
          },
        },
      };
    });

    setExtraInventory((prev) => {
      const next = { ...(prev || {}) };
      externalDays.forEach((d) => {
        const key = d.format("YYYY-MM-DD");
        if (preserveStaged && modifiedDates.has(key) && prev?.[key]) return;
        next[key] = loaded[key];
      });
      return next;
    });
  };

  const changeCalendarStart = async (rawDate) => {
    if (!rawDate || !selectedRoomTypeId) return;
    let next = moment(rawDate).startOf("day");
    if (!next.isValid()) return;
    if (next.isBefore(businessDate, "day")) next = businessDate.clone();

    setCalendarStart(next);
    setRangeLoading(true);
    try {
      await hydrateVisibleRange({
        roomTypeId: selectedRoomTypeId,
        startMoment: next,
        roomTypeList: roomTypes,
        preserveStaged: true,
      });
    } catch (err) {
      console.error(err);
      message.error(
        err?.response?.data?.message || "Could not load this calendar period."
      );
    } finally {
      setRangeLoading(false);
    }
  };

  const goPreviousRange = () => {
    const next = calendarStart.clone().subtract(DEFAULT_CALENDAR_WINDOW_DAYS, "day");
    changeCalendarStart(next.isBefore(businessDate, "day") ? businessDate : next);
  };

  const goNextRange = () =>
    changeCalendarStart(
      calendarStart.clone().add(DEFAULT_CALENDAR_WINDOW_DAYS, "day")
    );

  const goToday = () => changeCalendarStart(businessDate.clone());

  const loadSingleEditDate = async (dateKey) => {
    if (!dateKey || isVisibleMonthDate(dateKey) || extraInventory[dateKey]) return true;

    setEditDateLoading(true);
    try {
      const { rows, rateOverrides: savedRateOverrides } = await fetchMonthCalendar(
        selectedRoomTypeId,
        dateKey,
        dateKey
      );

      const totalRooms = Math.max(0, safeInt(selectedRoomType?.total_rooms, 0));
      let cell = {
        allotment: totalRooms,
        stopSell: false,
        minStay: 1,
        cta: false,
        ctd: false,
        rateCardId: null,
        overrideRate: null,
        currencyCode: DEFAULT_CURRENCY,
        rateOverrides: {},
        notes: "",
      };

      const row = (rows || []).find((r) => String(r.inventory_date).slice(0, 10) === dateKey);
      if (row) {
        cell = {
          ...cell,
          allotment: Math.max(0, safeInt(row.allotment, cell.allotment)),
          stopSell: !!row.stop_sell,
          minStay: Math.max(1, safeInt(row.min_stay, 1)),
          cta: !!row.closed_to_arrival,
          ctd: !!row.closed_to_departure,
          rateCardId: row.rate_card_id != null ? Number(row.rate_card_id) : null,
          overrideRate: row.override_rate != null ? Number(row.override_rate) : null,
          currencyCode: String(row.currency_code || DEFAULT_CURRENCY),
          notes: String(row.notes || ""),
        };
      }

      (savedRateOverrides || []).forEach((r) => {
        if (String(r.inventory_date).slice(0, 10) !== dateKey) return;
        cell.rateOverrides[String(Number(r.rate_id))] = {
          rate_id: Number(r.rate_id),
          occ1_rate: r.occ1_rate == null ? null : Number(r.occ1_rate),
          occ2_rate: r.occ2_rate == null ? null : Number(r.occ2_rate),
          guest_wise_rates: r.guest_wise_rates && typeof r.guest_wise_rates === "object" ? r.guest_wise_rates : {},
          currency_code: String(r.currency_code || DEFAULT_CURRENCY),
        };
      });

      setExtraInventory((prev) => ({
        ...prev,
        [dateKey]: prev[dateKey] || cell,
      }));
      return true;
    } catch (err) {
      console.error(err);
      message.error(err?.response?.data?.message || "Could not load the selected date.");
      return false;
    } finally {
      setEditDateLoading(false);
    }
  };

  const handleSingleEditDateChange = async (date) => {
    if (!date) return;
    const next = moment(date).startOf("day");
    if (next.isBefore(businessDate, "day")) return;

    const nextKey = next.format("YYYY-MM-DD");
    if (isVisibleMonthDate(nextKey) || extraInventory[nextKey]) {
      setEditDateKey(nextKey);
      return;
    }

    const loaded = await loadSingleEditDate(nextKey);
    if (loaded) setEditDateKey(nextKey);
  };

  const hydrateMonth = async ({ roomTypeId, monthMoment, roomTypeList = roomTypes }) => {
    const m = monthMoment.clone().startOf("month");
    const startDate = m.clone().startOf("month").format("YYYY-MM-DD");
    const endDate = m.clone().endOf("month").format("YYYY-MM-DD");
    const monthDays = buildMonthDays(m);

    // base defaults: allotment defaults to total_rooms if available
    const rt = roomTypeList.find((x) => x.id === roomTypeId);
    const defaults = buildDefaultInventoryMap(monthDays, rt?.total_rooms || 0);

    try {
      const { rows, rateOverrides } = await fetchMonthCalendar(roomTypeId, startDate, endDate);

      if (!rows.length && !rateOverrides.length) {
        setSoftInit(true);
        setInventory(defaults);
        lastServerSnapRef.current = { month: m.format("YYYY-MM"), roomTypeId, inventory: defaults };
        setDirty(false);
        setModifiedDates(new Set());
        return;
      }

      // map DB rows -> UI map
      const next = { ...defaults };

      rows.forEach((r) => {
        const key = String(r.inventory_date).slice(0, 10);
        if (!next[key]) return;

        next[key] = {
          allotment: Math.max(0, safeInt(r.allotment, next[key].allotment)),
          stopSell: !!r.stop_sell,
          minStay: Math.max(1, safeInt(r.min_stay, 1)),
          cta: !!r.closed_to_arrival,
          ctd: !!r.closed_to_departure,
          rateCardId: r.rate_card_id != null ? Number(r.rate_card_id) : null,
          overrideRate: r.override_rate != null ? Number(r.override_rate) : null,
          currencyCode: String(r.currency_code || DEFAULT_CURRENCY),
          notes: String(r.notes || ""),
        };
      });

      rateOverrides.forEach((r) => {
        const key = String(r.inventory_date).slice(0, 10);
        if (!next[key]) return;
        next[key] = { ...next[key], rateOverrides: { ...(next[key].rateOverrides || {}), [String(Number(r.rate_id))]: { rate_id: Number(r.rate_id), occ1_rate: r.occ1_rate == null ? null : Number(r.occ1_rate), occ2_rate: r.occ2_rate == null ? null : Number(r.occ2_rate), guest_wise_rates: r.guest_wise_rates || {}, currency_code: String(r.currency_code || DEFAULT_CURRENCY) } } };
      });

      setSoftInit(false);
      setInventory(next);
      lastServerSnapRef.current = { month: m.format("YYYY-MM"), roomTypeId, inventory: next };
      setDirty(false);
      setModifiedDates(new Set());
    } catch (err) {
      // enterprise UX: don’t block — show defaults + warning
      console.error(err);
      setSoftInit(true);
      setInventory(defaults);
      lastServerSnapRef.current = { month: m.format("YYYY-MM"), roomTypeId, inventory: defaults };
      setDirty(false);
      setModifiedDates(new Set());

      swalToast.fire({
        icon: "warning",
        title: "Showing safe defaults (calendar not loaded)",
      });
    }
  };

  const fetchAll = async () => {
    if (!ensureAuth()) return;
    setLoading(true);

    try {
      const rt = await fetchRoomTypes();
      if (!rt.length) {
        await Swal.fire({
          icon: "warning",
          title: "No room types found",
          text: "Create room types first, then return to manage manual inventory.",
          confirmButtonText: "OK",
        });
        setRoomTypes([]);
        setSelectedRoomTypeId(null);
        setInventory({});
        setExtraInventory({});
        setSoftInit(true);
        return;
      }

      setRoomTypes(rt);

      const firstId = selectedRoomTypeId || rt[0].id;
      setSelectedRoomTypeId(firstId);

      await Promise.all([fetchRateOptions(firstId), fetchPricingSettings()]);
      await hydrateMonth({ roomTypeId: firstId, monthMoment: month, roomTypeList: rt });
      await hydrateVisibleRange({
        roomTypeId: firstId,
        startMoment: calendarStart,
        roomTypeList: rt,
        preserveStaged: false,
      });
    } catch (err) {
      console.error(err);
      await Swal.fire({
        icon: "error",
        title: "Unable to load manual calendar",
        text: err?.response?.data?.message || "Please try again.",
        confirmButtonText: "OK",
      });

      setSoftInit(true);
      setRoomTypes([]);
      setSelectedRoomTypeId(null);
      setInventory({});
      setExtraInventory({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // when month changes (and we have a selected room type), reload month data
  useEffect(() => {
    if (!selectedRoomTypeId) return;
    if (!roomTypes.length) return;

    // if user has unsaved changes, don’t auto-nuke them silently
    const run = async () => {
      if (!dirty) {
        await hydrateMonth({ roomTypeId: selectedRoomTypeId, monthMoment: month });
        return;
      }

      const result = await Swal.fire({
        icon: "warning",
        title: "You have unsaved changes",
        text: "Switching month will discard all staged changes, including future-date edits, unless you save first.",
        showCancelButton: true,
        confirmButtonText: "Discard & switch",
        cancelButtonText: "Stay here",
      });

      if (result.isConfirmed) {
        setDirty(false);
        setModifiedDates(new Set());
        setExtraInventory({});
        await hydrateMonth({ roomTypeId: selectedRoomTypeId, monthMoment: month });
      } else {
        // revert month UI
        const snap = lastServerSnapRef.current;
        if (snap?.month) {
          setMonth(moment(snap.month, "YYYY-MM").startOf("month"));
        }
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month]);

  const onSelectRoomType = async (id) => {
    if (id === selectedRoomTypeId) return;

    if (dirty) {
      const result = await Swal.fire({
        icon: "warning",
        title: "You have unsaved changes",
        text: "Switching room type will discard all staged changes, including future-date edits, unless you save first.",
        showCancelButton: true,
        confirmButtonText: "Discard & switch",
        cancelButtonText: "Stay here",
      });
      if (!result.isConfirmed) return;
    }

    setSelectedRoomTypeId(id);
    setDirty(false);
    setModifiedDates(new Set());
    setExtraInventory({});
    setLoading(true);
    try {
      await fetchRateOptions(id);
      await hydrateMonth({ roomTypeId: id, monthMoment: month, roomTypeList: roomTypes });
      await hydrateVisibleRange({
        roomTypeId: id,
        startMoment: calendarStart,
        roomTypeList: roomTypes,
        preserveStaged: false,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDiscard = async () => {
    if (!dirty) {
      message.info("No changes to discard.");
      return;
    }
    const snap = lastServerSnapRef.current;
    if (!snap?.inventory) return;

    const result = await Swal.fire({
      icon: "warning",
      title: "Discard changes?",
      text: "Your unsaved changes will be lost.",
      showCancelButton: true,
      confirmButtonText: "Discard",
      cancelButtonText: "Keep editing",
    });

    if (result.isConfirmed) {
      setInventory({ ...(snap.inventory || {}) });
      setExtraInventory({});
      setDirty(false);
      setModifiedDates(new Set());
      await hydrateVisibleRange({
        roomTypeId: selectedRoomTypeId,
        startMoment: calendarStart,
        roomTypeList: roomTypes,
        preserveStaged: false,
      });
      message.success("Changes discarded.");
    }
  };

  const handleSave = async () => {
    if (!ensureAuth()) return;
    if (!validateBeforeSave()) return;

    setSaving(true);
    try {
      const payload = buildSavePayload();

      await axios.post(
        `${apiUrl}/booking-engine-settings/manual-calendar`, // ✅ correct route
        payload,
        authHeaders
      );

      message.success("Availability calendar saved.");
      setSoftInit(false);
      setDirty(false);
      setModifiedDates(new Set());
      setExtraInventory({});
      lastServerSnapRef.current = {
        month: month.format("YYYY-MM"),
        roomTypeId: selectedRoomTypeId,
        inventory: { ...inventory },
      };
      await hydrateVisibleRange({
        roomTypeId: selectedRoomTypeId,
        startMoment: calendarStart,
        roomTypeList: roomTypes,
        preserveStaged: false,
      });
    } catch (err) {
      console.error(err);
      await Swal.fire({
        icon: "error",
        title: "Save failed",
        text: err?.response?.data?.message || "We couldn’t save the manual calendar. Please try again.",
        confirmButtonText: "OK",
      });
    } finally {
      setSaving(false);
    }
  };

  const deleteDayOverride = async (dateKey) => {
    if (!ensureAuth()) return;
    if (!selectedRoomTypeId) return;

    const result = await Swal.fire({
      icon: "warning",
      title: "Remove saved override for this date?",
      text: "The saved manual override will be removed so your default inventory behavior can apply for this date.",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    try {
      setSaving(true);
      await axios.delete(`${apiUrl}/booking-engine-settings/manual-calendar`, {
        ...authHeaders,
        data: {
          be_room_type_id: Number(selectedRoomTypeId),
          inventory_date: dateKey,
        },
      });

      // after delete, set UI back to default day
      const totalRooms = selectedRoomType?.total_rooms || 0;
      patchDay(dateKey, {
        allotment: Math.max(0, safeInt(totalRooms, 0)),
        stopSell: false,
        minStay: 1,
        cta: false,
        ctd: false,
        rateCardId: null,
        overrideRate: null,
        currencyCode: DEFAULT_CURRENCY,
        rateOverrides: {},
        notes: "",
      });

      swalToast.fire({ icon: "success", title: "Day override deleted" });
    } catch (err) {
      console.error(err);
      message.error(err?.response?.data?.message || "Failed to delete day override.");
    } finally {
      setSaving(false);
    }
  };

  // --- derived stats for the visible 14-day operating window ---
  const rangeSummary = useMemo(() => {
    const keys = visibleDays.map((d) => d.format("YYYY-MM-DD"));
    let totalAllot = 0;
    let stopSellCount = 0;
    let rateOverrideCount = 0;
    let overInventoryCount = 0;
    let restrictedCount = 0;

    keys.forEach((key) => {
      const inBaseMonth = String(key).slice(0, 7) === month.format("YYYY-MM");
      const v = (inBaseMonth ? inventory[key] : extraInventory[key]) || {};
      const allotment = Math.max(0, safeInt(v.allotment, 0));
      totalAllot += allotment;
      if (v.stopSell) stopSellCount += 1;
      if (v.cta || v.ctd) restrictedCount += 1;
      if (
        (v.overrideRate != null && v.overrideRate !== "") ||
        Object.keys(v.rateOverrides || {}).length > 0
      ) {
        rateOverrideCount += 1;
      }
      if (
        selectedRoomType?.total_rooms > 0 &&
        allotment > selectedRoomType.total_rooms
      ) {
        overInventoryCount += 1;
      }
    });

    return {
      totalAllot,
      avgAllot: keys.length ? totalAllot / keys.length : 0,
      stopSellCount,
      restrictedCount,
      rateOverrideCount,
      overInventoryCount,
    };
  }, [visibleDays, inventory, extraInventory, selectedRoomType, month]);

  const futureStagedCount = Array.from(modifiedDates).filter(
    (dateKey) => String(dateKey).slice(0, 7) !== month.format("YYYY-MM")
  ).length;

  const rangeLabel = `${calendarStart.format("DD MMM YYYY")} – ${calendarEnd.format("DD MMM YYYY")}`;

  if (loading) {
    return (
      <div className="page-wrapper-full">
        <div className="content container-fluid">
          <Card className="shadow-sm" style={{ borderRadius: 14 }}>
            <Skeleton active paragraph={{ rows: 8 }} />
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper-full be-inventory-premium">
      <div className="content container-fluid" style={{ paddingBottom: dirty ? 88 : undefined, maxWidth: 1680, margin: "0 auto" }}>
        {/* Single premium page command bar */}
        <div
          style={{
            position: "sticky",
            top: 0,
            zIndex: 1000,
            background: "rgba(255,255,255,0.97)",
            backdropFilter: "blur(12px)",
            padding: "10px 0",
            borderBottom: "1px solid #e8edf4",
            marginBottom: 14,
          }}
        >
          <Row align="middle" justify="space-between" gutter={[14, 10]}>
            <Col flex="auto">
              <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
                <Button
                  aria-label="Back to Room Allocation"
                  icon={<ArrowLeft size={16} />}
                  onClick={() => navigate(returnTo)}
                  style={{ flex: "0 0 auto" }}
                />
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 11,
                    background: "#eef5ff",
                    display: "grid",
                    placeItems: "center",
                    color: "#1677ff",
                    flex: "0 0 auto",
                  }}
                >
                  <Calendar size={17} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <h3 style={{ margin: 0, color: "#111827", fontSize: 20, lineHeight: 1.2 }}>Availability & Inventory</h3>
                    <Tag color="blue" style={{ margin: 0, borderRadius: 999, paddingInline: 9 }}>Manual Inventory</Tag>
                    {dirty ? (
                      <Badge status="warning" text={`${modifiedDates.size} unsaved date${modifiedDates.size === 1 ? "" : "s"}`} />
                    ) : (
                      <Badge status="success" text="All changes saved" />
                    )}
                    {futureStagedCount > 0 ? (
                      <Tag style={{ margin: 0, borderRadius: 999, color: "#6941c6", borderColor: "#d9d6fe", background: "#f4f3ff" }}>
                        {futureStagedCount} future date{futureStagedCount === 1 ? "" : "s"}
                      </Tag>
                    ) : null}
                  </div>
                  <div style={{ color: "#667085", fontSize: 12, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    Rolling 14-day sellable inventory, restrictions and Booking Engine date-level rates.
                  </div>
                </div>
              </div>
            </Col>

            <Col flex="none">
              <Space size={8}>
                <Tooltip title={dirty ? "Save or discard changes before refreshing." : "Reload room types and inventory."}>
                  <Button icon={<RefreshCw size={15} />} onClick={fetchAll} disabled={saving || dirty}>
                    Refresh
                  </Button>
                </Tooltip>
                <Button icon={<RotateCcw size={15} />} onClick={handleDiscard} disabled={!dirty || saving}>
                  Discard
                </Button>
                <Button type="primary" icon={<Save size={15} />} onClick={handleSave} loading={saving} disabled={!dirty}>
                  Save Changes
                </Button>
              </Space>
            </Col>
          </Row>
        </div>

        {softInit && (
          <Alert
            type="info"
            showIcon
            style={{ borderRadius: 12, marginBottom: 14 }}
            message="Some dates are using room inventory defaults"
            description="Where no saved manual row exists, the room type’s configured physical inventory remains the safe default. Review, bulk update or fine-tune individual dates before saving."
          />
        )}

        {rangeSummary.overInventoryCount > 0 && (
          <Alert
            type="warning"
            showIcon
            closable
            style={{ borderRadius: 12, marginBottom: 14 }}
            message={`${rangeSummary.overInventoryCount} date${rangeSummary.overInventoryCount === 1 ? " has" : "s have"} availability above physical room inventory`}
            description="The current backend permits this. Review your overbooking policy before saving these dates."
          />
        )}

        {/* Scope and primary controls */}
        <Card
          className="shadow-sm be-inventory-control-card"
          style={{ borderRadius: 16, marginBottom: 12, border: "1px solid #e3e9f1", overflow: "hidden" }}
          bodyStyle={{ padding: 14 }}
        >
          <Row gutter={[16, 14]} align="middle">
            <Col xl={8} lg={8} md={12} xs={24}>
              <div style={{ fontSize: 11, color: "#7b8497", fontWeight: 800, textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 6 }}>
                Room type
              </div>
              <Select
                value={selectedRoomTypeId}
                onChange={onSelectRoomType}
                style={{ width: "100%" }}
                size="large"
                optionLabelProp="label"
              >
                {roomTypes.map((rt) => (
                  <Select.Option key={rt.id} value={rt.id} label={rt.name}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 14 }}>
                      <span style={{ fontWeight: 700 }}>{rt.name}</span>
                      <span style={{ color: "#8b94a7", fontSize: 12 }}>{rt.total_rooms || 0} rooms</span>
                    </div>
                  </Select.Option>
                ))}
              </Select>
              <div style={{ marginTop: 5, fontSize: 12, color: "#8b94a7" }}>
                {selectedRoomType?.total_rooms || 0} physical rooms configured
              </div>
            </Col>

            <Col xl={8} lg={8} md={12} xs={24}>
              <div style={{ fontSize: 11, color: "#7b8497", fontWeight: 800, textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 6 }}>
                Stay pricing method
              </div>
              <Select
                size="large"
                value={stayPricingMethod}
                loading={savingPricingMethod}
                disabled={savingPricingMethod}
                onChange={saveStayPricingMethod}
                style={{ width: "100%" }}
                options={[
                  { value: "nightly", label: "Nightly rate · Recommended" },
                  { value: "check_in", label: "Check-in date rate" },
                ]}
              />
              <div style={{ marginTop: 5, fontSize: 12, color: "#8b94a7" }}>
                {stayPricingMethod === "nightly" ? "Each occupied night uses that date’s selling rate." : "Arrival-date rate is locked for the complete stay."}
              </div>
            </Col>

            <Col xl={8} lg={8} md={12} xs={24}>
              <div style={{ fontSize: 11, color: "#7b8497", fontWeight: 800, textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 6 }}>
                Bulk changes
              </div>
              <Button
                type="primary"
                ghost
                size="large"
                block
                icon={<Sliders size={16} />}
                onClick={() => setBulkOpen(true)}
                disabled={saving || !selectedRoomType}
              >
                Bulk Update
              </Button>
              <div style={{ marginTop: 5, fontSize: 12, color: "#8b94a7" }}>
                Defaults to {calendarStart.format("MMMM YYYY")}; stage a range, then fine-tune individual dates.
              </div>
            </Col>
          </Row>
        </Card>

        {/* Operational summary */}
        <Row gutter={[8, 8]} style={{ marginBottom: 12 }}>
          {[
            { label: "Physical inventory", value: `${selectedRoomType?.total_rooms || 0}`, suffix: "rooms", icon: <Bed size={16} />, tone: "blue" },
            { label: "Avg. available", value: rangeSummary.avgAllot.toFixed(1), suffix: "per day", icon: <BarChart2 size={16} />, tone: "cyan" },
            { label: "Stop sell", value: rangeSummary.stopSellCount, suffix: "days", icon: <Slash size={16} />, tone: "red" },
            { label: "Restrictions", value: rangeSummary.restrictedCount, suffix: "days", icon: <Shield size={16} />, tone: "amber" },
            { label: "Rate overrides", value: rangeSummary.rateOverrideCount, suffix: "days", icon: <DollarSign size={16} />, tone: "violet" },
          ].map((item) => (
            <Col key={item.label} xl={4} lg={6} md={8} sm={12} xs={24} flex="1 1 165px">
              <div className={`be-inventory-stat be-inventory-stat-${item.tone}`}>
                <div className="be-inventory-stat-icon">{item.icon}</div>
                <div style={{ minWidth: 0 }}>
                  <div className="be-inventory-stat-label">{item.label}</div>
                  <div className="be-inventory-stat-value">
                    <span>{item.value}</span>
                    <small>{item.suffix}</small>
                  </div>
                </div>
              </div>
            </Col>
          ))}
        </Row>

        {/* Rolling 14-day distribution-style calendar */}
        <Card
          className="shadow-sm be-inventory-calendar-card be-direct-distribution-card"
          style={{ borderRadius: 16, border: "1px solid #e3e9f1", overflow: "hidden" }}
          bodyStyle={{ padding: 12 }}
        >
          <div className="be-dist-toolbar">
            <div>
              <div className="be-dist-kicker">Direct Booking Calendar</div>
              <div className="be-dist-roomline">
                <Bed size={16} />
                <strong>{selectedRoomType?.name || "Room Type"}</strong>
                <span>{selectedRoomType?.total_rooms || 0} physical rooms</span>
              </div>
            </div>

            <div className="be-dist-date-nav">
              <button
                type="button"
                className="be-dist-icon-btn"
                onClick={goPreviousRange}
                disabled={calendarStart.isSame(businessDate, "day") || rangeLoading}
                aria-label="Previous 14 days"
              >
                <ChevronLeft size={17} />
              </button>

              <DatePicker
                selected={calendarStart.toDate()}
                onChange={(date) => date && changeCalendarStart(date)}
                minDate={businessDate.toDate()}
                dateFormat="dd MMM yyyy"
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                popperClassName="be-dist-datepicker-popper"
                showPopperArrow={false}
                customInput={
                  <button type="button" className="be-dist-date-pill">
                    <Calendar size={15} />
                    <span>{calendarStart.format("DD MMM YYYY")}</span>
                  </button>
                }
              />

              <button
                type="button"
                className="be-dist-icon-btn"
                onClick={goNextRange}
                disabled={rangeLoading}
                aria-label="Next 14 days"
              >
                <ChevronRight size={17} />
              </button>

              {!calendarStart.isSame(businessDate, "day") ? (
                <button type="button" className="be-dist-today-btn" onClick={goToday} disabled={rangeLoading}>
                  Today
                </button>
              ) : null}
              {rangeLoading ? <Spin size="small" /> : null}
            </div>
          </div>

          <div className="be-dist-operating-model">
            <Info size={14} />
            <strong>Direct inventory</strong>
            <span className="be-dist-model-chip">Booking Engine</span>
            <span className="be-dist-model-copy">
              {rangeLabel} · Tap any date to manage availability, selling restrictions and direct pricing.
            </span>
          </div>

          <div className="be-dist-grid">
            {visibleDays.map((d) => {
              const key = d.format("YYYY-MM-DD");
              const cell = getInventoryCell(key);
              const isToday = d.isSame(businessDate, "day");
              const isWeekend = [0, 6].includes(d.day());
              const isModified = modifiedDates.has(key);
              const isRestricted = !!cell.cta || !!cell.ctd;
              const hasRateOverride =
                (cell.overrideRate != null && cell.overrideRate !== "") ||
                Object.keys(cell.rateOverrides || {}).length > 0;
              const allotment = Math.max(0, safeInt(cell.allotment, selectedRoomType?.total_rooms || 0));
              const minStay = Math.max(1, safeInt(cell.minStay, 1));
              const totalRooms = Math.max(0, safeInt(selectedRoomType?.total_rooms, 0));
              const exceedsInventory = totalRooms > 0 && allotment > totalRooms;
              const rateSummary = getDateRateSummary(cell);
              const soldOut = !cell.stopSell && allotment === 0;
              const statusClass = cell.stopSell ? "stop" : soldOut ? "soldout" : "live";
              const statusLabel = cell.stopSell ? "Stop Sell" : soldOut ? "Sold Out" : "Selling";

              return (
                <button
                  key={key}
                  type="button"
                  className={[
                    "be-dist-day-card",
                    `is-${statusClass}`,
                    isToday ? "is-today" : "",
                    isWeekend ? "is-weekend" : "",
                    isModified ? "is-edited" : "",
                    isRestricted ? "is-restricted" : "",
                  ].join(" ")}
                  onClick={() => openEdit(key)}
                  aria-label={`Edit ${selectedRoomType?.name || "room"} on ${d.format("DD MMM YYYY")}`}
                >
                  <div className="be-dist-day-head">
                    <div className="be-dist-day-date">
                      <span>{d.format("ddd")}</span>
                      <strong>{d.format("DD")}</strong>
                      {isToday ? <small>Today</small> : d.date() === 1 ? <small>{d.format("MMM")}</small> : null}
                    </div>
                    <span className={`be-dist-status ${statusClass}`}>{statusLabel}</span>
                  </div>

                  <div className={`be-dist-availability ${exceedsInventory ? "is-over" : ""}`}>
                    <span className="be-dist-avl-icon"><Bed size={14} /></span>
                    <strong>{allotment}</strong>
                    <small>/ {totalRooms}</small>
                  </div>

                  <div className="be-dist-bottom-grid">
                    <div className="be-dist-price-box">
                      <span className="be-dist-box-label"><DollarSign size={10} /> Direct</span>
                      <strong className={rateSummary.isOverride ? "is-override" : ""}>{rateSummary.label}</strong>
                      <small>{rateSummary.source}</small>
                    </div>
                    <div className="be-dist-minstay-box">
                      <span className="be-dist-box-label">Min stay</span>
                      <strong>{minStay}</strong>
                      <small>night{minStay === 1 ? "" : "s"}</small>
                    </div>
                  </div>

                  {(cell.cta || cell.ctd || hasRateOverride || isModified) ? (
                    <div className="be-dist-rule-chips">
                      {cell.cta ? <span>CTA</span> : null}
                      {cell.ctd ? <span>CTD</span> : null}
                      {hasRateOverride ? <span className="rate">Rate</span> : null}
                      {isModified ? <span className="edited">Unsaved</span> : null}
                    </div>
                  ) : null}
                </button>
              );
            })}
          </div>

          <div className="be-dist-footnote">
            <span><b>Selling</b> = available for direct booking</span>
            <span><b>Sold Out</b> = 0 rooms</span>
            <span><b>Stop Sell</b> = explicit closure</span>
            <span>Changes remain staged until <b>Save Changes</b>.</span>
          </div>
        </Card>

        <BeBulkUpdateDrawer
          open={bulkOpen}
          onClose={() => setBulkOpen(false)}
          onApply={applyBulkToCalendar}
          roomType={selectedRoomType}
          month={calendarStart.clone().startOf("month")}
          businessDate={businessDate}
          currency={DEFAULT_CURRENCY}
          rateOptions={rateOptions}
        />

        {dirty && (
          <div
            style={{
              position: "fixed",
              left: 0,
              right: 0,
              bottom: 16,
              zIndex: 950,
              pointerEvents: "none",
              display: "flex",
              justifyContent: "center",
              padding: "0 18px",
            }}
          >
            <div
              style={{
                pointerEvents: "auto",
                width: "min(760px, 100%)",
                border: "1px solid #dbe5f3",
                borderRadius: 14,
                background: "rgba(255,255,255,0.97)",
                backdropFilter: "blur(12px)",
                boxShadow: "0 16px 45px rgba(15,23,42,0.16)",
                padding: "11px 13px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 14,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                <div style={{ width: 34, height: 34, borderRadius: 10, background: "#fff7e6", color: "#d46b08", display: "grid", placeItems: "center" }}>
                  <Edit3 size={16} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 900, color: "#172033" }}>
                    {modifiedDates.size} date{modifiedDates.size === 1 ? "" : "s"} changed
                  </div>
                  <div style={{ color: "#7b8497", fontSize: 12, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    Review visible dates if needed, then save all staged dates once.
                  </div>
                </div>
              </div>
              <Space>
                <Button onClick={handleDiscard} disabled={saving}>Discard</Button>
                <Button type="primary" icon={<Save size={15} />} onClick={handleSave} loading={saving}>
                  Save Changes
                </Button>
              </Space>
            </div>
          </div>
        )}


        <style>{`
          .be-inventory-premium { background:linear-gradient(180deg,#f8fbff 0,#f5f7fb 260px,#f7f8fb 100%); min-height:100vh; }
          .be-inventory-control-card { box-shadow:0 8px 28px rgba(15,23,42,.055) !important; }
          .be-inventory-control-card .ant-select-selector, .be-inventory-control-card .ant-btn { border-radius:10px !important; }
          .be-inventory-stat {
            min-height:70px; border:1px solid #e4eaf2; background:rgba(255,255,255,.96); border-radius:14px; padding:11px 12px;
            display:flex; align-items:center; gap:10px; box-shadow:0 5px 18px rgba(15,23,42,.035);
          }
          .be-inventory-stat-icon { width:34px; height:34px; border-radius:10px; display:grid; place-items:center; background:#f3f6fa; color:#475569; flex:0 0 auto; }
          .be-inventory-stat-blue .be-inventory-stat-icon { background:#eef5ff; color:#155eef; }
          .be-inventory-stat-cyan .be-inventory-stat-icon { background:#ecfeff; color:#0e7490; }
          .be-inventory-stat-red .be-inventory-stat-icon { background:#fff1f2; color:#e11d48; }
          .be-inventory-stat-amber .be-inventory-stat-icon { background:#fff8e7; color:#b7791f; }
          .be-inventory-stat-violet .be-inventory-stat-icon { background:#f5f3ff; color:#7c3aed; }
          .be-inventory-stat-label { font-size:10px; color:#7b8497; font-weight:900; text-transform:uppercase; letter-spacing:.045em; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
          .be-inventory-stat-value { margin-top:3px; display:flex; align-items:baseline; gap:5px; }
          .be-inventory-stat-value span { font-size:20px; line-height:1; font-weight:900; color:#172033; }
          .be-inventory-stat-value small { font-size:10px; color:#94a3b8; font-weight:600; }
          .be-inventory-calendar-card { box-shadow:0 12px 34px rgba(15,23,42,.055) !important; }
          .be-inventory-calendar-head { display:flex; justify-content:space-between; align-items:center; gap:12px; flex-wrap:wrap; margin:-12px -12px 10px; padding:13px 14px 11px; background:linear-gradient(180deg,#fff,#fbfcfe); border-bottom:1px solid #edf1f5; }
          .be-inventory-grid-shell { padding:0 1px 2px; }
          .be-inventory-head-meta { display:flex; gap:7px; align-items:center; flex-wrap:wrap; }
          .be-inventory-glance-bar { margin:0 0 10px; padding:9px 11px; border:1px solid #e8edf4; border-radius:11px; background:#f8fafc; display:flex; align-items:center; gap:12px; flex-wrap:wrap; }
          .be-inventory-glance-title { font-size:11px; color:#172033; font-weight:900; text-transform:uppercase; letter-spacing:.05em; }
          .be-inventory-glance-items { display:flex; align-items:center; gap:12px; flex-wrap:wrap; }
          .be-inventory-glance-items span { display:flex; align-items:center; gap:5px; color:#536174; font-size:10.5px; font-weight:800; }
          .be-inventory-glance-items i { width:8px; height:8px; border-radius:50%; display:inline-block; }
          .be-inventory-glance-items .is-selling { background:#22c55e; }
          .be-inventory-glance-items .is-stop { background:#ef4444; }
          .be-inventory-glance-items .is-restrict { background:#f59e0b; }
          .be-inventory-glance-items .is-rate { background:#8b5cf6; }
          .be-inventory-glance-items .is-edited { background:#1677ff; }
          .be-inventory-glance-help { margin-left:auto; color:#8491a3; font-size:10.5px; }
          .be-inventory-empty-cell { min-height:142px; border-radius:13px; background:linear-gradient(135deg,#fafbfd,#f7f9fc); border:1px dashed #edf1f5; }
          .be-inventory-day { overflow:hidden; }
          .be-inventory-day::before { content:""; position:absolute; top:0; left:0; right:0; height:3px; background:#22c55e; z-index:1; }
          .be-inventory-day.is-modified::before { background:#1677ff; }
          .be-inventory-day.is-stop-sell::before { background:#ef4444; }
          .be-inventory-day.is-restricted:not(.is-stop-sell):not(.is-modified)::before { background:#f59e0b; }
          .be-day-head { padding:9px 9px 7px; display:flex; align-items:center; justify-content:space-between; gap:7px; border-bottom:1px solid #edf1f5; background:rgba(255,255,255,.72); }
          .be-day-date-wrap { display:flex; align-items:baseline; gap:5px; min-width:0; }
          .be-day-number { font-size:16px; line-height:1; color:#172033; font-weight:950; }
          .be-day-name { font-size:9px; color:#94a3b8; font-weight:900; text-transform:uppercase; letter-spacing:.05em; }
          .be-day-today { font-size:8px; color:#155eef; background:#eef5ff; border:1px solid #cfe0ff; border-radius:999px; padding:2px 5px; font-weight:900; text-transform:uppercase; }
          .be-day-selling-state { font-size:9px; line-height:1; font-weight:900; border-radius:999px; padding:5px 7px; white-space:nowrap; }
          .be-day-selling-state.is-open { color:#15803d; background:#f0fdf4; border:1px solid #bbf7d0; }
          .be-day-selling-state.is-closed { color:#b91c1c; background:#fef2f2; border:1px solid #fecaca; }
          .be-day-primary-grid { display:grid; grid-template-columns:minmax(0,.9fr) minmax(0,1.1fr); gap:8px; padding:8px 9px 4px; }
          .be-day-metric { min-width:0; }
          .be-day-metric + .be-day-metric { border-left:1px solid #edf1f5; padding-left:8px; }
          .be-day-metric-label { display:block; color:#8491a3; font-size:8.5px; font-weight:900; text-transform:uppercase; letter-spacing:.045em; white-space:nowrap; }
          .be-day-availability { margin-top:2px; color:#172033; font-size:19px; line-height:1.1; font-weight:950; }
          .be-day-availability small { color:#94a3b8; font-size:9px; font-weight:800; }
          .be-day-availability.is-over { color:#d97706; }
          .be-day-rate-value { margin-top:3px; color:#172033; font-size:14px; line-height:1.05; font-weight:950; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
          .be-day-rate-value.is-override { color:#6d28d9; }
          .be-day-rate-source { margin-top:2px; display:block; color:#94a3b8; font-size:8px; font-weight:800; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
          .be-inventory-capacity-track { height:3px; border-radius:999px; background:#edf1f5; overflow:hidden; margin:1px 9px 0; }
          .be-inventory-capacity-track span { display:block; height:100%; border-radius:inherit; background:linear-gradient(90deg,#62a4ff,#1677ff); }
          .be-day-rule-row { min-height:30px; padding:6px 9px 8px; display:flex; gap:4px; align-items:center; flex-wrap:wrap; }
          .be-day-minstay, .be-day-rule-chip { font-size:8.5px; line-height:1; font-weight:900; border-radius:999px; padding:4px 6px; white-space:nowrap; }
          .be-day-minstay { color:#475569; background:#f1f5f9; border:1px solid #e2e8f0; }
          .be-day-rule-chip.is-cta, .be-day-rule-chip.is-ctd { color:#a16207; background:#fffbeb; border:1px solid #fde68a; }
          .be-day-rule-chip.is-rate { color:#6d28d9; background:#f5f3ff; border:1px solid #ddd6fe; }
          .be-day-rule-chip.is-edited { color:#155eef; background:#eef5ff; border:1px solid #cfe0ff; }
          @media (max-width: 1100px) { .be-inventory-grid-shell { min-width:860px !important; } .be-inventory-glance-help { width:100%; margin-left:0; } }

          /* Booking Engine rolling calendar — visual parity with Channel Manager, scoped locally. */
          .be-direct-distribution-card .ant-card-body { padding:12px !important; }
          .be-dist-toolbar { display:flex; align-items:center; justify-content:space-between; gap:16px; margin-bottom:10px; }
          .be-dist-kicker { text-transform:uppercase; letter-spacing:.08em; font-size:10px; line-height:1.2; font-weight:900; color:#64748b; margin-bottom:7px; }
          .be-dist-roomline { display:flex; align-items:center; gap:8px; min-width:0; color:#0f172a; }
          .be-dist-roomline strong { font-size:14px; font-weight:900; }
          .be-dist-roomline span { color:#64748b; font-size:10px; white-space:nowrap; }
          .be-dist-date-nav { display:flex; align-items:center; gap:8px; }
          .be-dist-icon-btn, .be-dist-date-pill, .be-dist-today-btn { font:inherit; border:1px solid #dfe6ef; background:#fff; color:#0f172a; cursor:pointer; box-shadow:none; transition:border-color .15s ease, box-shadow .15s ease, background .15s ease; }
          .be-dist-icon-btn { width:35px; height:35px; border-radius:11px; display:grid; place-items:center; padding:0; }
          .be-dist-date-pill { min-height:35px; border-radius:11px; display:inline-flex; align-items:center; gap:8px; padding:0 12px; font-size:12px; font-weight:900; }
          .be-dist-today-btn { min-height:35px; border-radius:10px; padding:0 11px; font-size:10px; font-weight:900; }
          .be-dist-icon-btn:hover:not(:disabled), .be-dist-date-pill:hover, .be-dist-today-btn:hover:not(:disabled) { border-color:#b7cdf8; box-shadow:0 4px 12px rgba(37,99,235,.08); }
          .be-dist-icon-btn:disabled, .be-dist-today-btn:disabled { opacity:.42; cursor:not-allowed; }
          .be-dist-operating-model { min-height:36px; display:flex; align-items:center; gap:8px; padding:8px 10px; margin-bottom:10px; border-radius:11px; background:#f8fafc; border:1px solid #edf1f5; color:#64748b; font-size:9.5px; }
          .be-dist-operating-model > svg { color:#2563eb; flex:0 0 auto; }
          .be-dist-operating-model strong { color:#0f172a; font-size:10px; white-space:nowrap; }
          .be-dist-model-chip { border-radius:999px; border:1px solid #bfdbfe; background:#eff6ff; color:#1d4ed8; padding:3px 7px; font-size:8px; font-weight:900; white-space:nowrap; }
          .be-dist-model-copy { white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
          .be-dist-grid { display:grid; grid-template-columns:repeat(7,minmax(145px,1fr)); gap:10px; }
          .be-dist-day-card { appearance:none; font:inherit; box-sizing:border-box; text-align:left; position:relative; overflow:hidden; min-height:184px; border:1px solid #dfe7f0; border-top:3px solid #22c55e; border-radius:14px; background:#fff; padding:10px; color:#0f172a; cursor:pointer; box-shadow:0 4px 14px rgba(15,23,42,.035); transition:transform .15s ease, box-shadow .15s ease, border-color .15s ease; }
          .be-dist-day-card:hover { transform:translateY(-2px); box-shadow:0 10px 24px rgba(15,23,42,.09); border-color:#cbd9eb; }
          .be-dist-day-card.is-weekend { background:#fcfdff; }
          .be-dist-day-card.is-stop { border-top-color:#ef4444; }
          .be-dist-day-card.is-soldout { border-top-color:#f97316; }
          .be-dist-day-card.is-restricted:not(.is-stop):not(.is-soldout) { border-top-color:#f59e0b; }
          .be-dist-day-card.is-edited { box-shadow:inset 0 0 0 1px #60a5fa,0 6px 18px rgba(37,99,235,.09); }
          .be-dist-day-card.is-today { border-color:#93c5fd; border-top-color:#2563eb; background:linear-gradient(180deg,#fbfdff,#fff); }
          .be-dist-day-head { display:flex; align-items:flex-start; justify-content:space-between; gap:8px; margin-bottom:9px; }
          .be-dist-day-date { width:48px; min-height:48px; padding:6px 7px; border:1px solid #dfe7f0; border-radius:10px; background:linear-gradient(180deg,#f8fafc,#f1f5f9); display:flex; flex-direction:column; align-items:flex-start; justify-content:center; line-height:1; }
          .be-dist-day-date span { color:#64748b; font-size:8px; font-weight:900; text-transform:uppercase; letter-spacing:.06em; }
          .be-dist-day-date strong { margin-top:4px; color:#0f172a; font-size:17px; line-height:1; font-weight:950; }
          .be-dist-day-date small { margin-top:3px; color:#2563eb; font-size:7px; line-height:1; font-weight:900; text-transform:uppercase; }
          .be-dist-status { display:inline-flex; align-items:center; gap:4px; border-radius:999px; padding:5px 7px; font-size:8px; line-height:1; font-weight:900; white-space:nowrap; border:1px solid; }
          .be-dist-status::before { content:""; width:5px; height:5px; border-radius:999px; background:currentColor; }
          .be-dist-status.live { color:#059669; background:#ecfdf5; border-color:#a7f3d0; }
          .be-dist-status.stop { color:#dc2626; background:#fef2f2; border-color:#fecaca; }
          .be-dist-status.soldout { color:#c2410c; background:#fff7ed; border-color:#fed7aa; }
          .be-dist-availability { min-height:54px; border:1px solid #bfdbfe; border-radius:12px; background:#eff6ff; display:flex; align-items:center; gap:8px; padding:9px 10px; margin-bottom:8px; }
          .be-dist-avl-icon { width:24px; height:24px; border-radius:8px; display:grid; place-items:center; color:#2563eb; background:#fff; }
          .be-dist-availability strong { font-size:25px; line-height:1; font-weight:950; color:#0f172a; }
          .be-dist-availability small { margin-left:-3px; color:#64748b; font-size:9px; font-weight:800; }
          .be-dist-availability.is-over strong { color:#d97706; }
          .be-dist-bottom-grid { display:grid; grid-template-columns:minmax(0,1.35fr) minmax(0,.65fr); gap:7px; }
          .be-dist-price-box, .be-dist-minstay-box { min-width:0; min-height:57px; border-radius:11px; padding:8px 9px; }
          .be-dist-price-box { border:1px solid #a7f3d0; background:#f0fdf4; }
          .be-dist-minstay-box { border:1px solid #e2e8f0; background:#f8fafc; }
          .be-dist-box-label { display:flex; align-items:center; gap:4px; color:#64748b; font-size:7px; line-height:1; font-weight:900; text-transform:uppercase; letter-spacing:.05em; }
          .be-dist-price-box .be-dist-box-label { color:#047857; }
          .be-dist-price-box strong, .be-dist-minstay-box strong { display:block; margin-top:5px; color:#0f172a; font-size:13px; line-height:1; font-weight:950; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
          .be-dist-price-box strong.is-override { color:#6d28d9; }
          .be-dist-price-box small, .be-dist-minstay-box small { display:block; margin-top:3px; color:#94a3b8; font-size:7px; font-weight:800; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
          .be-dist-rule-chips { display:flex; align-items:center; gap:4px; flex-wrap:wrap; margin-top:7px; }
          .be-dist-rule-chips span { border-radius:999px; padding:3px 5px; background:#fffbeb; border:1px solid #fde68a; color:#a16207; font-size:7px; line-height:1; font-weight:900; }
          .be-dist-rule-chips span.rate { background:#f5f3ff; border-color:#ddd6fe; color:#6d28d9; }
          .be-dist-rule-chips span.edited { background:#eff6ff; border-color:#bfdbfe; color:#1d4ed8; }
          .be-dist-footnote { display:flex; align-items:center; gap:14px; flex-wrap:wrap; margin-top:10px; color:#64748b; font-size:9px; }
          .be-dist-footnote b { color:#334155; }
          .be-dist-datepicker-popper { z-index:100100 !important; }
          @media (max-width:1200px) { .be-dist-grid { grid-template-columns:repeat(4,minmax(145px,1fr)); } }
          @media (max-width:820px) { .be-dist-toolbar { align-items:flex-start; flex-direction:column; } .be-dist-date-nav { width:100%; flex-wrap:wrap; } .be-dist-operating-model { align-items:flex-start; flex-wrap:wrap; } .be-dist-model-copy { width:100%; white-space:normal; } .be-dist-grid { grid-template-columns:repeat(2,minmax(145px,1fr)); } }
          @media (max-width:520px) { .be-dist-grid { grid-template-columns:1fr; } }
        `}</style>

        {/* Edit availability drawer */}
        <Drawer
          open={open}
          onClose={() => setOpen(false)}
          width="min(760px, 100vw)"
          destroyOnClose={false}
          zIndex={100000}
          getContainer={() => document.body}
          closeIcon={<X size={19} strokeWidth={2} />}
          className="be-edit-availability-drawer"
          title={
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                <Edit3 size={16} color="#1677ff" />
                <span style={{ fontWeight: 900 }}>Edit Availability</span>
              </div>
              <div style={{ marginTop: 3, fontSize: 12, color: "#7b8497", fontWeight: 500 }}>
                Fine-tune one date without leaving the rolling inventory workspace.
              </div>
            </div>
          }
          footer={
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
              <div style={{ color: "#7b8497", fontSize: 12 }}>
                Changes are <b style={{ color: "#172033" }}>staged only</b> until page-level Save Changes.
              </div>
              <Button type="primary" icon={<Check size={15} />} onClick={() => setOpen(false)}>
                Done
              </Button>
            </div>
          }
          bodyStyle={{ padding: "18px 22px 28px", background: "#f7f9fc" }}
          headerStyle={{ padding: "18px 22px", borderBottom: "1px solid #e7ecf3", background: "#fff" }}
          footerStyle={{ padding: "13px 22px", borderTop: "1px solid #e7ecf3", background: "rgba(255,255,255,.98)", backdropFilter: "blur(12px)" }}
        >
          {editDateKey ? (
            <div className="be-edit-drawer-body">
              <div className="be-edit-scope-card">
                <div style={{ fontSize: 11, color: "#7b8497", textTransform: "uppercase", letterSpacing: ".07em", fontWeight: 800 }}>
                  Editing scope
                </div>
                <div className="be-edit-scope-grid">
                  <div>
                    <div style={{ fontWeight: 900, color: "#172033", fontSize: 15 }}>{selectedRoomType?.name || "Room Type"}</div>
                    <div style={{ color: "#7b8497", fontSize: 12, marginTop: 2 }}>
                      {selectedRoomType?.total_rooms || 0} physical rooms · {moment(editDateKey).format("MMMM YYYY")}
                    </div>
                  </div>
                  <div className="be-edit-date-control">
                    <div className="be-edit-control-label">Edit date</div>
                    <div className="be-react-date-wrap">
                      <Calendar size={15} className="be-react-date-icon" />
                      <DatePicker
                        selected={moment(editDateKey, "YYYY-MM-DD").toDate()}
                        onChange={handleSingleEditDateChange}
                        minDate={businessDate.toDate()}
                        monthsShown={2}
                        showMonthDropdown
                        showYearDropdown
                        dropdownMode="select"
                        disabled={editDateLoading}
                        dateFormat="dd MMM yyyy (EEE)"
                        className="form-control be-react-date-input"
                        calendarClassName="be-react-date-calendar"
                        popperClassName="be-edit-datepicker-popper"
                        showPopperArrow={false}
                      />
                      {editDateLoading ? <Spin size="small" className="be-edit-date-loading" /> : null}
                    </div>
                  </div>
                  <div className="be-edit-scope-badges">
                    <Tag color="blue" style={{ margin: 0 }}>{moment(editDateKey).format("ddd")}</Tag>
                    <Tag color={stayPricingMethod === "nightly" ? "processing" : "geekblue"} style={{ margin: 0 }}>
                      {stayPricingMethod === "nightly" ? "Nightly pricing" : "Check-in rate"}
                    </Tag>
                  </div>
                </div>
              </div>

              <div className="be-edit-section-card">
                <div className="be-edit-section-head">
                  <div className="be-edit-section-icon"><Bed size={16} /></div>
                  <div>
                    <div className="be-edit-section-title">Selling controls</div>
                    <div className="be-edit-section-subtitle">Availability and sell-state for this room type on the selected date.</div>
                  </div>
                </div>

                <div className="be-edit-field-card be-edit-field-card-toggle">
                  <div>
                    <div className="be-edit-field-title">Stop sell</div>
                    <div className="be-edit-field-help">Close this room type for new Booking Engine reservations on this date.</div>
                  </div>
                  <Switch checked={!!editCell?.stopSell} onChange={(v) => patchDay(editDateKey, { stopSell: v })} />
                </div>

                <div className="be-edit-two-col">
                  <div className="be-edit-input-card">
                    <div className="be-edit-field-head">
                      <div className="be-edit-field-title">Available rooms</div>
                      <span>{selectedRoomType?.total_rooms || 0} physical</span>
                    </div>
                    <InputNumber
                      min={0}
                      max={999}
                      style={{ width: "100%" }}
                      value={safeInt(editCell?.allotment, 0)}
                      onChange={(v) => patchDay(editDateKey, { allotment: Math.max(0, safeInt(v, 0)) })}
                    />
                  </div>

                  <div className="be-edit-input-card">
                    <div className="be-edit-field-title" style={{ marginBottom: 7 }}>Minimum stay</div>
                    <InputNumber
                      min={1}
                      max={30}
                      addonAfter="nights"
                      style={{ width: "100%" }}
                      value={Math.max(1, safeInt(editCell?.minStay, 1))}
                      onChange={(v) => patchDay(editDateKey, { minStay: Math.max(1, safeInt(v, 1)) })}
                    />
                  </div>
                </div>
              </div>

              <div className="be-edit-section-card">
                <div className="be-edit-section-head">
                  <div className="be-edit-section-icon"><Shield size={16} /></div>
                  <div>
                    <div className="be-edit-section-title">Arrival & departure restrictions</div>
                    <div className="be-edit-section-subtitle">Control whether stays may begin or end on this date.</div>
                  </div>
                </div>
                <div className="be-edit-two-col">
                  <div className="be-edit-field-card be-edit-field-card-toggle">
                    <div>
                      <div className="be-edit-field-title">Closed to Arrival</div>
                      <div className="be-edit-field-help">Existing stays can continue, but guests cannot check in.</div>
                    </div>
                    <Switch checked={!!editCell?.cta} onChange={(v) => patchDay(editDateKey, { cta: v })} />
                  </div>
                  <div className="be-edit-field-card be-edit-field-card-toggle">
                    <div>
                      <div className="be-edit-field-title">Closed to Departure</div>
                      <div className="be-edit-field-help">Guests cannot select this date as check-out.</div>
                    </div>
                    <Switch checked={!!editCell?.ctd} onChange={(v) => patchDay(editDateKey, { ctd: v })} />
                  </div>
                </div>
              </div>

              <div className="be-edit-section-card">
                <div className="be-edit-section-head be-edit-rate-head">
                  <div className="be-edit-section-icon"><DollarSign size={16} /></div>
                  <div style={{ flex: 1 }}>
                    <div className="be-edit-section-title">Booking Engine rate overrides</div>
                    <div className="be-edit-section-subtitle">Date-specific prices affect Booking Engine only. OTA and PMS rates stay independent.</div>
                  </div>
                  <Tag color={stayPricingMethod === "nightly" ? "blue" : "geekblue"} style={{ margin: 0 }}>
                    {stayPricingMethod === "nightly" ? "Nightly pricing" : "Check-in rate pricing"}
                  </Tag>
                </div>

                {rateOptions.length && editCell?.overrideRate != null ? (
                  <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 12, borderRadius: 12 }}
                    message={`Legacy date base override: ${editCell?.currencyCode || DEFAULT_CURRENCY} ${Number(editCell?.overrideRate || 0).toLocaleString()}`}
                    description={
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                        <span>This older single-rate override still applies to the default RO/first Booking Engine rate unless a specific override exists.</span>
                        <Button size="small" onClick={() => patchDay(editDateKey, { overrideRate: null })}>Clear legacy rate</Button>
                      </div>
                    }
                  />
                ) : null}

                {rateOptions.length ? (
                  <div style={{ display: "grid", gap: 10 }}>
                    {rateOptions.map((rate) => {
                      const ro = editCell?.rateOverrides?.[String(rate.rate_id)] || {};
                      const guestRates = rate.guest_wise_rates || {};
                      return (
                        <div key={rate.rate_id} className={`be-edit-rate-card ${Object.keys(ro).length ? "is-overridden" : ""}`}>
                          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, marginBottom: 10 }}>
                            <div>
                              <div style={{ fontWeight: 900, color: "#172033" }}>{rate.display_label || rate.plan_code || `Rate ${rate.rate_id}`}</div>
                              <div style={{ fontSize: 11, color: "#8b94a7", marginTop: 2 }}>{rate.plan_code || "BE"} · blank values inherit the master rate</div>
                            </div>
                            {Object.keys(ro).length ? <Tag color="purple">Overridden</Tag> : <Tag>Master</Tag>}
                          </div>

                          {rate.is_guest_wise_rate ? (
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(120px,1fr))", gap: 8 }}>
                              {Object.keys(guestRates).sort((a,b) => Number(a)-Number(b)).map((guestCount) => (
                                <div key={guestCount}>
                                  <div className="be-edit-control-label">{guestCount} guest{Number(guestCount) === 1 ? "" : "s"}</div>
                                  <InputNumber
                                    min={0}
                                    style={{ width: "100%" }}
                                    value={ro.guest_wise_rates?.[guestCount] ?? null}
                                    placeholder={`${DEFAULT_CURRENCY} ${Number(guestRates[guestCount] || 0).toLocaleString()}`}
                                    onChange={(v) => patchRateOverride(editDateKey, rate.rate_id, {
                                      guest_wise_rates: { ...(ro.guest_wise_rates || {}), [guestCount]: safeNumber(v, null) },
                                      currency_code: DEFAULT_CURRENCY,
                                    })}
                                  />
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="be-edit-two-col">
                              <div>
                                <div className="be-edit-control-label">Single</div>
                                <InputNumber min={0} style={{ width: "100%" }} value={ro.occ1_rate ?? null} placeholder={`${DEFAULT_CURRENCY} ${Number(rate.occ1_rate || 0).toLocaleString()}`} onChange={(v) => patchRateOverride(editDateKey, rate.rate_id, { occ1_rate: safeNumber(v, null), currency_code: DEFAULT_CURRENCY })} />
                              </div>
                              <div>
                                <div className="be-edit-control-label">Double</div>
                                <InputNumber min={0} style={{ width: "100%" }} value={ro.occ2_rate ?? null} placeholder={`${DEFAULT_CURRENCY} ${Number(rate.occ2_rate || 0).toLocaleString()}`} onChange={(v) => patchRateOverride(editDateKey, rate.rate_id, { occ2_rate: safeNumber(v, null), currency_code: DEFAULT_CURRENCY })} />
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="be-edit-two-col">
                    <div>
                      <div className="be-edit-control-label">Date base rate</div>
                      <InputNumber min={0} style={{ width: "100%" }} value={editCell?.overrideRate ?? null} onChange={(v) => patchDay(editDateKey, { overrideRate: safeNumber(v, null) })} placeholder="Date base rate" />
                    </div>
                    <div>
                      <div className="be-edit-control-label">Currency</div>
                      <Input value={editCell?.currencyCode || DEFAULT_CURRENCY} onChange={(e) => patchDay(editDateKey, { currencyCode: (e.target.value || DEFAULT_CURRENCY).toUpperCase() })} />
                    </div>
                    <div style={{ gridColumn: "1 / -1", color: "#8b94a7", fontSize: 11 }}>No active Booking Engine rate options found. The legacy date base rate will be used and the existing occupancy differential preserved.</div>
                  </div>
                )}
              </div>

              <div className="be-edit-section-card">
                <div className="be-edit-section-head">
                  <div className="be-edit-section-icon"><Edit3 size={16} /></div>
                  <div>
                    <div className="be-edit-section-title">Internal note</div>
                    <div className="be-edit-section-subtitle">Operational context only; never shown to the guest.</div>
                  </div>
                </div>
                <Input.TextArea
                  rows={3}
                  value={editCell?.notes || ""}
                  onChange={(e) => patchDay(editDateKey, { notes: e.target.value })}
                  placeholder="Add an internal note for this date"
                  maxLength={500}
                  showCount
                />
              </div>

              <div className="be-edit-danger-zone">
                <div>
                  <div style={{ fontWeight: 900, color: "#9f1239", fontSize: 13 }}>Remove saved date override</div>
                  <div style={{ color: "#9f5b68", fontSize: 11, marginTop: 3 }}>Returns this date to its default inventory behaviour. This action is immediate.</div>
                </div>
                <Tooltip title="This removes the saved override immediately and returns this date to default inventory behavior.">
                  <Button danger icon={<Trash2 size={14} />} onClick={() => deleteDayOverride(editDateKey)}>
                    Remove override
                  </Button>
                </Tooltip>
              </div>

              <style>{`
                .be-edit-availability-drawer .ant-drawer-content { background:#f7f9fc; }
                .be-edit-availability-drawer .ant-drawer-header-title { align-items:flex-start; }
                .be-edit-availability-drawer .ant-drawer-close {
                  width:36px; height:36px; border-radius:10px; display:grid; place-items:center;
                  color:#475569; background:#f8fafc; border:1px solid #e2e8f0; margin-inline-end:12px;
                }
                .be-edit-availability-drawer .ant-drawer-close:hover { background:#eef4ff; color:#155eef; border-color:#c7d7fe; }
                .be-edit-drawer-body { display:grid; gap:14px; }
                .be-edit-scope-card {
                  background:linear-gradient(135deg,#eef5ff 0%,#ffffff 60%); border:1px solid #dfe8f5;
                  border-radius:16px; padding:16px 17px; box-shadow:0 6px 22px rgba(15,23,42,.04);
                }
                .be-edit-scope-grid { display:grid; grid-template-columns:minmax(170px,1fr) minmax(230px,280px) auto; gap:14px; align-items:end; margin-top:8px; }
                .be-edit-scope-badges { display:flex; gap:6px; justify-content:flex-end; align-items:center; flex-wrap:wrap; padding-bottom:2px; }
                .be-edit-section-card { background:#fff; border:1px solid #e6ebf2; border-radius:16px; padding:16px 17px; box-shadow:0 7px 24px rgba(15,23,42,.035); }
                .be-edit-section-head { display:flex; gap:10px; align-items:center; margin-bottom:13px; }
                .be-edit-section-icon { width:34px; height:34px; display:grid; place-items:center; border-radius:10px; background:#eef5ff; color:#155eef; flex:0 0 auto; }
                .be-edit-section-title { font-size:14px; font-weight:900; color:#172033; }
                .be-edit-section-subtitle { margin-top:2px; font-size:11px; color:#7b8497; line-height:1.45; }
                .be-edit-field-card { border:1px solid #e7ebf2; border-radius:12px; background:#fff; padding:12px 13px; }
                .be-edit-field-card-toggle { display:flex; align-items:center; justify-content:space-between; gap:18px; }
                .be-edit-field-title { font-size:13px; font-weight:900; color:#172033; }
                .be-edit-field-help { font-size:11px; color:#8490a3; line-height:1.45; margin-top:3px; }
                .be-edit-field-head { display:flex; justify-content:space-between; align-items:center; gap:8px; margin-bottom:7px; }
                .be-edit-field-head span { font-size:10px; color:#94a3b8; font-weight:700; }
                .be-edit-input-card { border:1px solid #e7ebf2; border-radius:12px; background:#fbfcfe; padding:12px 13px; }
                .be-edit-two-col { display:grid; grid-template-columns:1fr 1fr; gap:10px; }
                .be-edit-control-label { font-size:11px; font-weight:800; color:#64748b; text-transform:uppercase; letter-spacing:.04em; margin-bottom:6px; }
                .be-edit-rate-card { background:#fff; border:1px solid #e7ebf2; border-radius:12px; padding:12px; transition:all .15s ease; }
                .be-edit-rate-card.is-overridden { border-color:#d9c8ff; background:linear-gradient(135deg,#fcfaff,#fff); box-shadow:0 5px 15px rgba(124,58,237,.045); }
                .be-edit-danger-zone { display:flex; align-items:center; justify-content:space-between; gap:14px; flex-wrap:wrap; padding:14px 15px; border-radius:14px; border:1px solid #fecdd3; background:#fff7f8; }
                .be-react-date-wrap { position:relative; }
                .be-react-date-wrap .react-datepicker-wrapper, .be-react-date-wrap .react-datepicker__input-container { width:100%; }
                .be-react-date-icon { position:absolute; left:12px; top:50%; transform:translateY(-50%); z-index:2; color:#155eef; pointer-events:none; }
                .be-edit-date-loading { position:absolute; right:10px; top:10px; z-index:3; }
                .be-react-date-input { height:40px; padding-left:37px !important; border-color:#dfe5ee !important; border-radius:9px !important; color:#172033 !important; font-size:13px !important; font-weight:800 !important; background:#fff !important; }
                .be-react-date-input:focus { border-color:#8bb7ff !important; box-shadow:0 0 0 3px rgba(21,94,239,.08) !important; }
                .be-edit-datepicker-popper { z-index:100050 !important; }
                .be-react-date-calendar { border:1px solid #dfe7f2 !important; box-shadow:0 18px 45px rgba(15,23,42,.16); border-radius:14px !important; overflow:hidden; font-family:inherit; }
                .be-react-date-calendar .react-datepicker__header { background:#f8fbff; border-bottom:1px solid #e6edf6; }
                .be-react-date-calendar .react-datepicker__day--selected, .be-react-date-calendar .react-datepicker__day--keyboard-selected { background:#1677ff; border-radius:8px; }
                @media (max-width: 767px) {
                  .be-edit-scope-grid { grid-template-columns:1fr; align-items:stretch; }
                  .be-edit-scope-badges { justify-content:flex-start; }
                  .be-edit-two-col { grid-template-columns:1fr; }
                  .be-edit-rate-head { align-items:flex-start; flex-wrap:wrap; }
                }
              `}</style>
            </div>
          ) : null}
        </Drawer>

        {/* Global saving overlay */}
        {saving && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(255,255,255,0.35)",
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backdropFilter: "blur(4px)",
            }}
          >
            <Card style={{ borderRadius: 14 }}>
              <Spin />
              <div style={{ marginTop: 10, fontWeight: 900 }}>Saving manual calendar…</div>
              <div style={{ color: "#6b7280", marginTop: 4 }}>Please don’t close this tab.</div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default BeManualInventoryCalendar;
